import React from 'react';
import SlidePanel from '../shared/SlidePanel';
import VoucherCard from './VoucherCard';
import Toggle from './Toggle';
import {
  getVouchersUsingDesign,
} from './data';
import '../shared/ui.css';

// DesignPreviewPanel — right-side slide-out shown when the salon clicks
// a design card in the library.
//
// Mirrors the catalogue's ServiceDetailPanel pattern:
//   - SlidePanel's standard mode (title in the sticky header)
//   - Body sections use the shared .beu-section-label utility
//   - Single primary action ("Assign to a voucher") pinned at the bottom
//   - Edit (Custom only) lives in headerRight so the footer stays clean
//
// Availability is a Toggle row inside the body — consistent with how
// availability is set on the design cards in the grid behind the panel.

const ordinal = (n) => {
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const formatLastSold = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `Last sold ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

const buildPreviewVoucher = (design /* salonName unused — kept by API */) => {
  if (design.isCustom) {
    return {
      name: 'Gift Card',
      template: 'signature',
      theme: design.theme,
      value: 50,
      promo: null,
      // Full config passes through so the preview reflects exactly what
      // the salon saved — layout, bg mode, gradient, focal, brand mark.
      customDesign: design.config,
    };
  }
  return {
    name: 'Gift Card',
    template: design.occasion || 'signature',
    theme: design.theme,
    value: 50,
    promo: null,
  };
};

// ───── Icon set ─────
const TrendUp = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 9l4-4 2 2 4-4" />
    <path d="M8 3h4v4" />
  </svg>
);
const TrendDown = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 3l4 4 2-2 4 4" />
    <path d="M8 9h4V5" />
  </svg>
);
const EditIcon = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 2l3 3-7 7H2v-3l7-7z" />
  </svg>
);

const DesignPreviewPanel = ({
  design,
  designs,
  vouchers = [],
  stats = {},
  salonName = 'Your Salon',
  currency = '£',
  onClose,
  onToggle,
  onEdit,
  onAssignToVoucher,
  onOpenVoucher,
}) => {
  if (!design) return null;

  // Real sales-derived stats for this design (undefined when it has no sales).
  const ds = stats[design.id] || null;
  const hasSales = !!(ds && ds.sold > 0);
  const trendPct = ds && ds.trendPct !== null && ds.trendPct !== undefined ? ds.trendPct : null;
  const trendIsUp = (trendPct || 0) >= 0;
  const trendAbs = Math.abs(trendPct || 0);
  const fmtMoney = (n) => `${currency}${Math.round(n || 0).toLocaleString('en-GB')}`;

  const usingVouchers = getVouchersUsingDesign(vouchers, design);
  const totalVouchers = vouchers.length;

  const previewVoucher = buildPreviewVoucher(design, salonName);

  // Single footer action — matches the catalogue panel's "Edit service".
  // Make-available/hide is a Toggle inside the body now (see Availability
  // section below) since it's a state-flip, not a navigation action.
  const footer = (
    <button
      type="button"
      className="sp-btn-primary"
      onClick={() => { if (onAssignToVoucher) onAssignToVoucher(design); }}
    >
      Assign to a voucher
    </button>
  );

  // Custom designs get an inline Edit pill in the header (next to the
  // close button) — same affordance the cards in the grid use, so the
  // shape is familiar.
  const headerRight = design.isCustom ? (
    <button
      type="button"
      className="v2-pp-header-action"
      onClick={() => { if (onEdit) onEdit(design); }}
      aria-label={`Edit ${design.label}`}
    >
      <EditIcon /> Edit
    </button>
  ) : null;

  return (
    <SlidePanel
      title={design.label}
      onClose={onClose}
      headerRight={headerRight}
      footer={footer}
      width={520}
      className="voucher-v2 v2-design-preview-panel"
    >
      {/* Hero — light surface so the card visually pops off the white body */}
      <div className="v2-pp-stage">
        <VoucherCard
          voucher={previewVoucher}
          aesthetic={design.aesthetic}
          salonName={salonName}
          currency={currency}
          showPromo={false}
        />
      </div>

      {/* Stats card — REAL sales-derived figures (sold / revenue / rank by
          revenue / trend). Empty state until this design has actually sold.
          Pick-rate & impressions are intentionally absent (need event
          tracking — Phase 2). */}
      {hasSales ? (
        <>
          <div className="v2-pp-stats">
            <div className="v2-pp-stat">
              <div className="v2-pp-stat-num">{ds.sold}</div>
              <div className="v2-pp-stat-label">sold</div>
            </div>
            <div className="v2-pp-stat">
              <div className="v2-pp-stat-num">{fmtMoney(ds.revenue)}</div>
              <div className="v2-pp-stat-label">revenue</div>
            </div>
            <div className="v2-pp-stat">
              <div className="v2-pp-stat-num">{ds.rank ? ordinal(ds.rank) : '—'}</div>
              <div className="v2-pp-stat-label">
                by revenue{ds.totalDesignsRanked > 1 ? ` (of ${ds.totalDesignsRanked})` : ''}
              </div>
            </div>
            <div className="v2-pp-stat">
              {trendPct !== null ? (
                <div className={`v2-pp-stat-num v2-pp-trend ${trendIsUp ? 'up' : 'down'}`}>
                  {trendIsUp ? <TrendUp /> : <TrendDown />} {trendAbs}%
                </div>
              ) : (
                <div className="v2-pp-stat-num">{ds.sharePct}%</div>
              )}
              <div className="v2-pp-stat-label">{trendPct !== null ? 'vs last month' : 'of gift-card revenue'}</div>
            </div>
          </div>

          <div className="v2-pp-activity">
            {formatLastSold(ds.lastSoldAt) || 'Sold recently'} · {ds.sharePct}% of gift-card revenue
          </div>
        </>
      ) : (
        <div className="v2-pp-activity">
          No sales yet — this design hasn’t been bought on a gift card.
        </div>
      )}

      {/* Availability — labelled section + Toggle on the right */}
      <div className="v2-pp-section">
        <div className="v2-pp-section-header">
          <p className="beu-section-label">Available in library</p>
          <Toggle
            on={design.available}
            onChange={(next) => onToggle(design.id, next)}
            ariaLabel={`${design.available ? 'Hide' : 'Show'} ${design.label}`}
          />
        </div>
        <p className="v2-pp-help">
          {design.available
            ? 'Appears in the picker on every voucher you create. Assign it to specific vouchers from the editor.'
            : 'Hidden from the library. No voucher will offer this design until you re-enable it.'}
        </p>
      </div>

      {/* Used by — chip list of vouchers offering this design */}
      <div className="v2-pp-section">
        <div className="v2-pp-section-header">
          <p className="beu-section-label">Used by</p>
          <span className="v2-pp-section-count">
            {usingVouchers.length} of {totalVouchers} vouchers
          </span>
        </div>
        {usingVouchers.length === 0 ? (
          <div className="v2-pp-empty">
            No vouchers offer this design yet. Make it available and assign it
            to a voucher in the editor.
          </div>
        ) : (
          <div className="v2-pp-chips">
            {usingVouchers.map((v) => (
              <button
                key={v.id}
                type="button"
                className="v2-pp-chip"
                onClick={() => { if (onOpenVoucher) onOpenVoucher(v); }}
                title={`Open ${v.name}`}
              >
                <span className="v2-pp-chip-dot" />
                {v.name}
              </button>
            ))}
          </div>
        )}
        <p className="v2-pp-help">
          Vouchers with a curated subset only show this design when it's in
          their list. Vouchers with no curation show every available design.
        </p>
      </div>
    </SlidePanel>
  );
};

export default DesignPreviewPanel;
